import type { MenuItemType } from "antd/es/menu/interface"
import React from "react"

import { NotificationBadge } from "../../../components/Shared/NotificationBadge.tsx"
import type { SidebarItem } from "../sidebar-config.tsx"

// Keyed by the same nav `key` used in sidebar-config.tsx (top-level items
// and Approval's three children alike) — the single map SidebarBody builds
// from `state.notificationCounts` and passes in here.
export type NavBadgeCounts = Record<string, number>

// The badge belongs to the label, not to a right-aligned column: normal
// inline flow (icon → label → small gap → badge), never justify-between or
// a fixed-width slot, so a short label ("Projects") and a long one
// ("Document Review") both read the count as part of the name rather than
// pinned to the row's far edge.
const withLabelBadge = (
  label: React.ReactNode,
  count: number | undefined
): React.ReactNode => {
  if (!count) return label
  return (
    <span className="inline-flex items-center gap-2">
      <span>{label}</span>
      <NotificationBadge count={count} />
    </span>
  )
}

// The collapsed Sider hides the label entirely, leaving the icon as the only
// surface for a top-level row — a plain dot (no number) keeps a minimal
// indication alive there without reviving a right-aligned/corner badge once
// the label is back (expanded, or a submenu's flyout popup).
const withIconDot = (
  icon: React.ReactNode,
  count: number | undefined
): React.ReactNode => {
  if (!icon || !count) return icon
  return (
    <span className="relative inline-flex">
      {icon}
      <span
        className="absolute -right-0.5 -top-0.5 size-1.5 rounded-full"
        style={{ backgroundColor: "#B91C1C" }}
      />
    </span>
  )
}

export const applyNotificationBadges = (
  items: SidebarItem[],
  counts: NavBadgeCounts
): SidebarItem[] =>
  items.map((item) => {
    const key = String(item.key)

    if ("children" in item && item.children?.length) {
      return {
        ...item,
        icon: withIconDot(item.icon, counts[key]),
        label: withLabelBadge(item.label, counts[key]),
        children: item.children.map((child) => {
          const childItem = child as MenuItemType | null
          if (!childItem) return child
          const childKey = String(childItem.key)
          return {
            ...childItem,
            label: withLabelBadge(childItem.label, counts[childKey])
          }
        })
      }
    }

    if (!(key in counts)) return item

    return {
      ...item,
      icon: withIconDot(item.icon, counts[key]),
      label: withLabelBadge(item.label, counts[key])
    }
  })
